import { defineComponent, h, warn, } from '@vue/runtime-core';
// A record of modal entries keyed by their IDs. This is necessary so that only
// the `stack` array in `ModalStackComponent` is _reactive_ while all the
// details associated with their life cycle is not!
const entries = {};
// Current (available) `ModalStack`
let modalStack = undefined;
// Our modal stack component, rendering one after another all open modals
export const ModalStackComponent = defineComponent({
    emits: ['modals', 'modalCreated', 'modalDismissed'],
    data() {
        return { stack: [] };
    },
    computed: {
        hasModals() {
            return this.stack.length > 0;
        },
    },
    // Mounted here simply warns the about using <modal-stack/> multiple times
    mounted() {
        if (modalStack)
            warn('Vue modal stack component mounted multiple times');
        else
            modalStack = this;
    },
    // BeforeUnmount removes the ability to create modals
    beforeUnmount() {
        if (modalStack === this)
            modalStack = undefined;
    },
    // Render our stack of modals, one after another
    render() {
        return this.stack.map((id) => {
            const entry = entries[id];
            // If we created a vnode before, simply return it
            if (entry.vnode)
                return entry.vnode;
            // Dismiss the modal, removing it from the stack, removing it from our
            // keyed hash of modals, and finally resolving the promise with the result
            const onDismissModal = (result) => {
                const index = this.stack.indexOf(id);
                if (index >= 0)
                    this.stack.splice(index, 1);
                delete entries[id];
                entry.resolve(result);
                // Emit our events
                this.$emit('modalCreated', this.hasModals);
                this.$emit('modals', this.hasModals);
            };
            // Render our component in the VNODE tree tracking `dismissModal` events
            const componentVNode = h(entry.component, Object.assign(Object.assign({}, entry.props), { onDismissModal }));
            // Emit our events
            this.$emit('modalDismissed', this.hasModals);
            this.$emit('modals', this.hasModals);
            // Nicely wrap our modal component in a DIV: one modal, one DIV
            return entry.vnode = h('div', { [`data-modal-${id}`]: '' }, componentVNode);
        });
    },
});
/* ========================================================================== *
 * CREATE MODAL                                                               *
 * ========================================================================== */
// Push a new modal in our modals stack
export function createModal(component, props) {
    return new Promise((resolve, reject) => {
        if (!modalStack) {
            warn('No ModalStack component mounted in current render tree');
            reject(new Error('No ModalStack avaible'));
        }
        // Get a random ID we can use to identify our DIVs
        const id = Math.floor((Math.random() * Number.MAX_SAFE_INTEGER))
            .toString(16).substr(0, 8).padStart(8, '0');
        // Push our ID, component, props and deferring methods in the stack
        entries[id] = { component, props, resolve };
        modalStack === null || modalStack === void 0 ? void 0 : modalStack.stack.push(id);
    });
}
/**
 * The `vue-modals` plugin, instrumenting the `$createModal()` function on
 * components instances and providing the `<modal-stack/>` component.
 */
export const modals = {
    install(app) {
        app.config.globalProperties.$createModal = createModal;
        app.component('ModalStack', ModalStackComponent);
    },
};
//# sourceMappingURL=plugin.js.map